import { useState } from "react";
import { subDays, startOfDay, endOfDay } from "date-fns";
import { Filter } from "lucide-react";
import { AnalyticsFiltersDrawer } from "@/features/analytics/components/AnalyticsFiltersDrawer";
import { AnalyticsResultsPanel } from "@/features/analytics/components/AnalyticsResultsPanel";
import { useAnalyticsApp } from "@/features/analytics/context";
import { useAnalyticsReport } from "@/features/analytics/hooks/useAnalyticsReport";
import type {
  AnalyticsDateRange,
  AnalyticsFilters,
} from "@/features/analytics/types";
import {
  countActiveAnalyticsFilters,
  createEmptyAnalyticsFilters,
  getAnalyticsResultsLabel,
  cloneAnalyticsFilters,
} from "@/features/analytics/utils/analytics";
import DateRangePicker from "@/components/common/DateRangePicker";
import { usePageTitle } from "@/components/common/AppShell";

type PickerRange = { from: Date; to: Date };

const defaultRange = (): PickerRange => {
  const to = endOfDay(new Date());
  const from = startOfDay(subDays(new Date(), 6));
  return { from, to };
};

const AnalyticsPage = (): JSX.Element => {
  usePageTitle("Analytics");
  const { campaigns, availableChannels, availableCategories } =
    useAnalyticsApp();
  const [pickerRange, setPickerRange] = useState<PickerRange>(defaultRange);
  const [isFiltersDrawerOpen, setIsFiltersDrawerOpen] = useState(false);
  const [appliedFilters, setAppliedFilters] = useState<AnalyticsFilters>(
    createEmptyAnalyticsFilters(),
  );
  const [draftFilters, setDraftFilters] = useState<AnalyticsFilters>(
    createEmptyAnalyticsFilters(),
  );

  const selectedRange: AnalyticsDateRange = {
    startDate: pickerRange.from,
    endDate: pickerRange.to,
  };

  const {
    rows,
    isLoading,
    isRequestCancelable,
    isRunReportButtonDisabled,
    runReportButtonLabel,
    errorMessage,
    handleRunReportButtonClick,
  } = useAnalyticsReport({
    selectedRange,
    appliedFilters,
  });

  const activeFiltersCount = countActiveAnalyticsFilters(appliedFilters);

  const handleOpenFilters = (): void => {
    setDraftFilters(cloneAnalyticsFilters(appliedFilters));
    setIsFiltersDrawerOpen(true);
  };

  const handleCloseFilters = (): void => {
    setIsFiltersDrawerOpen(false);
  };

  const handleApplyFilters = (): void => {
    setAppliedFilters(cloneAnalyticsFilters(draftFilters));
    setIsFiltersDrawerOpen(false);
  };

  const handleResetFilters = (): void => {
    setDraftFilters(createEmptyAnalyticsFilters());
  };

  return (
    <section className="flex min-w-0 flex-col">
      <div className="mb-[26px] flex flex-wrap items-start justify-between gap-4">
        <p className="text-[13.5px] text-(--text-muted)">
          Review daily impression totals and unique device trends.
        </p>
        <div className="flex flex-wrap items-center gap-3">
          <DateRangePicker value={pickerRange} onChange={setPickerRange} />
          <button
            type="button"
            onClick={handleOpenFilters}
            className="inline-flex items-center gap-2 rounded-(--radius-md) border border-(--border-strong) bg-(--surface-1) px-[14px] py-[9px] text-[13px] font-semibold text-(--text-primary) transition-colors hover:border-(--brand-primary)"
          >
            <Filter
              size={15}
              strokeWidth={1.8}
              className="text-(--brand-primary)"
            />
            Filters
            {activeFiltersCount > 0 && (
              <span className="inline-flex min-w-[22px] items-center justify-center rounded-(--radius-pill) bg-(--pastel-violet) px-2 py-0.5 text-[11px] font-bold text-(--brand-primary)">
                {activeFiltersCount}
              </span>
            )}
          </button>
          <button
            type="button"
            onClick={handleRunReportButtonClick}
            disabled={isRunReportButtonDisabled}
            className={`inline-flex items-center gap-2 rounded-(--radius-md) px-[18px] py-[10px] text-[13px] font-bold transition-colors ${
              isRequestCancelable
                ? "bg-(--status-danger) text-white hover:opacity-90"
                : isRunReportButtonDisabled
                  ? "cursor-not-allowed bg-(--brand-primary)/60 text-(--brand-primary-contrast)"
                  : "bg-(--brand-primary) text-(--brand-primary-contrast) hover:bg-(--brand-primary-hover)"
            }`}
          >
            {runReportButtonLabel}
          </button>
        </div>
      </div>

      {errorMessage ? (
        <div className="mb-4 rounded-(--radius-md) border border-(--status-danger)/30 bg-(--pastel-rose) px-4 py-3 text-[13px] text-(--status-danger)">
          {errorMessage}
        </div>
      ) : null}

      <AnalyticsResultsPanel
        rows={rows}
        isLoading={isLoading}
        resultsLabel={getAnalyticsResultsLabel(rows.length)}
      />

      <AnalyticsFiltersDrawer
        isOpen={isFiltersDrawerOpen}
        value={draftFilters}
        campaigns={campaigns}
        channels={availableChannels}
        categories={availableCategories}
        onChange={setDraftFilters}
        onClose={handleCloseFilters}
        onApply={handleApplyFilters}
        onReset={handleResetFilters}
      />
    </section>
  );
};

export default AnalyticsPage;
